import React, { useCallback, useEffect, useState } from "react";
import { Search, CalendarDays, CircleX, UserCheck } from "lucide-react";
import { formatDate, numberWithCommas, exchangeDate, debounce } from "../../helpers/index.js";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { getPractice } from "../../helpers/index.js";
import DataTablePagination from "../DataTablePagination.jsx";
import Spacing from "../Spacing.jsx";
import DatePicker from "react-datepicker";
import EligibilityLogsDataTable from "../DataTable/EligibilityLogsDataTable.jsx";
import { fetchEligibilityLogs } from "../../services/eligibilityLogs.js";

export default function EligibilityLogs({ selectedPracticeChange, fetchChangePracticeData }) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [eligibilityLogsData, setEligibilityLogsData] = useState([]);
  const [dateRange, setDateRange] = useState(["", ""]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    search_term: "",
    start_date: "",
    end_date: "",
    account_id: account_id,
    location_id: location_id,
    order_by: [],
  });

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });


  useEffect(() => {
    loadEligibilityLogsData(filter);
  }, [filter]);

  const loadEligibilityLogsData = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const response = await fetchEligibilityLogs(filter);
      setEligibilityLogsData(response?.data?.results || []);
      setPaginationCount({
        totalCount: response?.data?.count,
        totalPages: response?.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadEligibilityLogsData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleDateChange = (update) => {
    const [start, end] = update;

    if (start && end && formatDate(start) === formatDate(end)) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    } else if (start && end) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(end),
      });
    } else if (start) {
      setFilter({
        ...filter,
        start_date: formatDate(start),
        end_date: formatDate(start),
      });
    }
    setDataLoading(true)

    setDateRange([start, end]);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const channel = new BroadcastChannel('tab_practice_change');
  useEffect(() => {
    channel.onmessage = (event) => {
      setFilter({
        ...filter,
        location_id: event?.data?.location_id,
        account_id: event?.data?.account_id,
      });
      location_id = event?.data?.location_id;
      account_id = event?.data?.account_id;
    };
    return () => {
      channel.close();
    };
  }, [channel, location_id, account_id]);

  useDocumentTitle("Eligibility Logs");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24" style={{ marginBottom: "0px" }}>
          <i className="docpro_blue_bg docpro_white_color">
            <UserCheck width={20} height={20} />
          </i>
          Eligibility Logs
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="5" sm="5" />

      <div className="docpro_data_table_wrap">
        <div className={`docpro_data_table_headings small_heading docpro_grid_gap_10 ${filter.start_date === filter.end_date ? "docpro_have_date_filter" : "" }`}>
          <div className={`searching_div docpro_grid_gap_10 ${(filter.start_date && (filter.start_date === filter.end_date) && (paginationCount.totalCount > 0) ) ? "docpro_sm_height_lg_42" : ""}`}>
            <form className="docpro_search_form">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
            <div className={`flex items-center docpro_grid_gap_4 docpro_date_filter`}>
              <div className={`docpro_toggle_calendar ${filter.start_date ? "active" : "" }`}>
                <CalendarDays />

                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  onChange={handleDateChange}
                />
                {filter.start_date && filter.end_date && (
                  <>
                    <span>{exchangeDate(filter.start_date)}</span>
                    {filter.start_date !== filter.end_date && (
                      <span>{exchangeDate(filter.end_date)}</span>
                    )}
                  </>
                )}
              </div>
              {filter.start_date && (
                <span onClick={() => {
                  setFilter({
                    ...filter,
                    start_date: "",
                    end_date: "",
                  });
                  setDateRange([null, null]);
                  setDataLoading(true);
                }}>
                  <CircleX size={22} color="var(--accent)" className="pointer" />
                </span>
              )}
            </div>
          </div>
        </div>

        <EligibilityLogsDataTable
          tableData={eligibilityLogsData}
          reloadData={loadEligibilityLogsData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(eligibilityLogsData.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}
